import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AB_INDETERMINATE_VALUE } from "abreact/input/AbIndeterminateValue";
import { AbSizeContext, sizeProviderWrapper } from "abreact/misc/AbSize";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import * as React from "react";
import * as AbHooks from "abreact/utils/AbHooks";
export { AB_INDETERMINATE_VALUE };

/**
 * The properties for an AbCheckbox.
 */

/**
 * The **AbCheckbox** component allows the user to toggle a value on or off.
 */
export const AbCheckbox = props => {
  const inputElement = React.useRef(null);
  const uniqueId = AbHooks.useUniqueId("checkbox");
  const contextSize = React.useContext(AbSizeContext);
  const onKeyDown = e => {
    // if key value is relevant and the checkbox is in a checkbox group, capture the event.
    if ([KeyValue.ARROW_UP, KeyValue.ARROW_RIGHT, KeyValue.ARROW_DOWN, KeyValue.ARROW_LEFT].includes(e.key) && e.currentTarget.parentElement?.parentElement?.classList.contains("ab-checkbox-group")) {
      e.preventDefault();
      // Get all checkboxes in this group
      const checkboxes = Array.from(e.currentTarget.parentElement?.parentElement?.querySelectorAll(`.ab-checkbox-group > .ab-checkbox:not(.ab-disabled) > .ab-checkbox-input, #${uniqueId}`) ?? []);
      const thisIndex = checkboxes.findIndex(element => element.id === uniqueId);
      if (thisIndex >= 0) {
        let nextButton;
        if ([KeyValue.ARROW_UP, KeyValue.ARROW_LEFT].includes(e.key)) {
          nextButton = checkboxes[(thisIndex + checkboxes.length - 1) % checkboxes.length];
        } else {
          nextButton = checkboxes[(thisIndex + 1) % checkboxes.length];
        }
        nextButton.focus();
      }
    }
  };
  const onChange = event => {
    const value = event.target.checked;
    props.onChange(value);
  };
  const {
    name,
    label,
    value,
    tooltip
  } = props;
  const {
    id,
    className,
    style,
    disabled,
    ...checkboxInputProps
  } = ReactUtils.inputProps("ab-checkbox", props, ["ab-single-line", props.size ?? contextSize]);
  // Manually remove data-test-id because deconstruction syntax can't.
  delete checkboxInputProps["data-test-id"];
  if (inputElement.current) {
    inputElement.current.indeterminate = value === AB_INDETERMINATE_VALUE;
  }
  const inputClassName = ReactUtils.classNamesString(["ab-checkbox-input", "abui", value === AB_INDETERMINATE_VALUE && "ab-indeterminate", value === true && "ab-checked"]);
  const inner = /*#__PURE__*/React.createElement(AbWithTooltip, {
    tooltip: tooltip
  }, /*#__PURE__*/React.createElement("label", {
    style: style,
    className: className,
    id: id,
    "data-test-id": props.dataTestId,
    htmlFor: uniqueId,
    onClick: e => {
      // Each click event inside the label is doubled due to default label behavior. This filters out half the clicks.
      if (e.target !== inputElement.current) {
        e.stopPropagation();
        return;
      }
      props.eventHandlers?.onClick?.(e);
    }
  }, /*#__PURE__*/React.createElement("input", _extends({
    className: inputClassName,
    id: uniqueId,
    type: "checkbox",
    name: name
    // In debugging tools, "checked" doesn't display correctly:
    // https://stackoverflow.com/questions/51743810/chrome-devtools-not-showing-checked-or-checked-true-attribute-in-html-tag
    ,
    checked: value === true,
    ref: input => {
      inputElement.current = input;
      if (inputElement.current) {
        inputElement.current.indeterminate = value === AB_INDETERMINATE_VALUE;
      }
    },
    onChange: disabled ? undefined : onChange,
    readOnly: disabled || props.onChange === undefined,
    onKeyDown: onKeyDown
  }, checkboxInputProps)), /*#__PURE__*/React.createElement("div", {
    className: "ab-checkbox-visual"
  }, /*#__PURE__*/React.createElement("div", {
    className: "ab-checkbox-check"
  })), label));
  return sizeProviderWrapper(inner, props, contextSize);
};
//# sourceMappingURL=AbCheckbox.js.map
